// SPEC-15 · Entrar. Email and password, "Esqueci-me da palavra-passe",
// "Entrar", then "Continuar com Google". The screen checks what it can and
// shows what went wrong; the session itself opens behind the SignIn seam.
//
// Every open session lands on the Avaliações hub (SPEC-12, question 2);
// a Google account new to PYF goes to Dados gerais instead.
//
// SPEC-31: once the session is open, the account's language is read and
// wins over this device's; it arrives while the next screen shows.

import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

import { clearAttempts, lockNow, lockedUntil, recordFailure } from "../account/attempts";
import { SIGN_IN_TIMEOUT_MS, SignInError } from "../account/signIn";
import type { SignIn as SignInCalls, SignInFailure } from "../account/signIn";
import { emailLooksValid } from "../account/signUpForm";
import { Field } from "../components/Field";
import type { Copy } from "../i18n/language";
import { useCopy, useLanguage } from "../i18n/LanguageProvider";
import { forgetPlaces } from "../lib/place";
import { Link, paths, useRouter } from "../lib/router";
import { Timeout, within } from "../lib/within";

type ErrorKey = keyof Copy["signIn"]["errors"];

/** What the screen is waiting on; one thing at a time. */
type Busy = "password" | "google" | "reset" | null;

const button =
  "flex h-[var(--control-h)] w-full items-center justify-center rounded-pill text-body font-semibold " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf";

/** A rejection as the screens name it: a timeout reads as no network. */
function failureOf(error: unknown, step: string): SignInFailure {
  if (error instanceof SignInError) return error.failure;
  console.error(`[sign-in] ${step}: no answer after the wait`, { kind: error instanceof Error ? error.name : typeof error });
  return "offline";
}

export function SignIn({ signIn }: { signIn: SignInCalls }) {
  const { navigate } = useRouter();
  const { syncWithAccount } = useLanguage();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState<Busy>(null);
  const [emailError, setEmailError] = useState<ErrorKey | null>(null);
  const [passwordError, setPasswordError] = useState<ErrorKey | null>(null);
  const [banner, setBanner] = useState<ErrorKey | null>(null);
  const [resetSentTo, setResetSentTo] = useState<string | null>(null);
  const [heldUntil, setHeldUntil] = useState(() => lockedUntil(Date.now()));
  /** Numbers the sign-ins started, and keeps the number of the last one that opened a session. */
  const attempts = useRef(0);
  const opened = useRef(0);

  // The button comes back by itself when the 10 minutes are over.
  useEffect(() => {
    if (heldUntil === null) return;
    const timer = setTimeout(() => {
      setHeldUntil(null);
      setBanner((shown) => (shown === "tooManyAttempts" ? null : shown));
    }, heldUntil - Date.now());
    return () => clearTimeout(timer);
  }, [heldUntil]);

  const text = useCopy().signIn;
  const held = heldUntil !== null;
  const normalizedEmail = email.trim().toLowerCase();

  const clearMessages = () => {
    setEmailError(null);
    setPasswordError(null);
    setBanner(null);
    setResetSentTo(null);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (busy || held || normalizedEmail === "" || password === "") return;
    clearMessages();
    setBusy("password");
    const number = ++attempts.current;
    const attempt = signIn.withPassword(normalizedEmail, password);
    try {
      await within(attempt, SIGN_IN_TIMEOUT_MS);
      opened.current = number;
      clearAttempts();
      // A new session starts from nothing the device remembered (SPEC-27).
      forgetPlaces();
      syncWithAccount();
      // Replace, so the back gesture never returns to a form already sent.
      navigate(paths.conversa, { replace: true });
      return;
    } catch (error) {
      // The screen said "Sem ligação": an answer arriving after that must
      // not leave a session open on a phone she may have handed on.
      // Only a newer sign-in that went through, by password or Google, is hers to keep.
      if (error instanceof Timeout) {
        void attempt.then(
          () => (opened.current > number ? undefined : signIn.signOut()),
          () => {},
        );
      }
      const failure = failureOf(error, "password");
      if (failure === "wrong-credentials" || failure === "too-many-attempts") {
        // The email stays; the password goes (SPEC-15, Action list).
        setPassword("");
        const until = failure === "too-many-attempts" ? lockNow(Date.now()) : recordFailure(Date.now());
        if (until === null) {
          setPasswordError("wrongCredentials");
        } else {
          setHeldUntil(until);
          setBanner("tooManyAttempts");
        }
      } else {
        setBanner(failure === "offline" ? "offline" : "failed");
      }
    }
    setBusy(null);
  };

  const google = async () => {
    if (busy) return;
    clearMessages();
    setBusy("google");
    const number = ++attempts.current;
    try {
      // No timeout: the athlete is the one taking her time on Google's sheet.
      const outcome = await signIn.withGoogle();
      if (outcome !== "cancelled") {
        opened.current = number;
        clearAttempts();
        forgetPlaces();
        syncWithAccount();
        navigate(outcome === "assessments" ? paths.conversa : paths.profileOnboarding, { replace: true });
        return;
      }
    } catch (error) {
      const failure = failureOf(error, "google");
      if (failure === "offline") setBanner("offline");
      else if (failure === "account-exists") setBanner("accountExists");
      else setBanner("failed");
    }
    setBusy(null);
  };

  const forgot = async () => {
    if (busy) return;
    clearMessages();
    if (!emailLooksValid(normalizedEmail)) {
      setEmailError("emailFirst");
      return;
    }
    setBusy("reset");
    try {
      await within(signIn.sendPasswordReset(normalizedEmail), SIGN_IN_TIMEOUT_MS);
      setResetSentTo(normalizedEmail);
    } catch (error) {
      setBanner(failureOf(error, "reset email") === "offline" ? "offline" : "resetFailed");
    }
    setBusy(null);
  };

  return (
    <main className="screen-enter flex min-h-dvh flex-col bg-cream px-5 pt-5">
      <header className="flex items-center justify-between">
        <Link
          to={paths.welcome}
          className="flex size-[var(--touch-min)] items-center justify-center rounded-pill bg-violet text-title text-cream no-underline hover:bg-violet-ink hover:text-cream"
        >
          <span aria-hidden="true">←</span>
          <span className="sr-only">{text.back}</span>
        </Link>
        <Link to={paths.signUp} className="text-body font-semibold text-violet no-underline">
          {text.signUp}
        </Link>
      </header>

      <h1 className="pt-6 text-display leading-[var(--lh-display)] font-semibold tracking-[var(--ls-display)]">
        {text.title}
      </h1>
      <p className="pt-3 text-body leading-[var(--lh-body)] text-muted">{text.lead}</p>

      <form noValidate onSubmit={submit} className="flex flex-1 flex-col pt-8">
        <div className="flex flex-col gap-5">
          <Field
            label={text.emailLabel}
            placeholder={text.emailPlaceholder}
            type="email"
            inputMode="email"
            autoComplete="email"
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            error={emailError ? text.errors[emailError] : null}
          />
          {/* No placeholder: grey dots in an emptied field read as a password still typed. */}
          <Field
            label={text.passwordLabel}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            error={passwordError ? text.errors[passwordError] : null}
          />
        </div>

        <div className="flex flex-col items-end gap-2 pt-3">
          <button
            type="button"
            onClick={forgot}
            disabled={busy !== null}
            className="min-h-[var(--touch-min)] text-body-sm font-semibold text-violet hover:text-violet-ink disabled:text-disabled"
          >
            {text.forgot}
          </button>
          {resetSentTo && (
            <p role="status" className="text-right text-caption text-muted">
              {text.resetSent.before}
              <span className="font-semibold text-dark">{resetSentTo}</span>
              {text.resetSent.after}
            </p>
          )}
        </div>

        <div className="mt-auto flex flex-col gap-4 pt-8 pb-6">
          <button
            type="submit"
            disabled={busy !== null || held || normalizedEmail === "" || password === ""}
            className={`${button} bg-dark text-cream hover:bg-dark-hover disabled:bg-line-10 disabled:text-disabled`}
          >
            {busy === "password" ? text.submitting : text.submit}
          </button>
          {banner && (
            <p role="alert" className="rounded-card-inner bg-orange-tint px-4 py-3 text-center text-body-sm text-orange-ink">
              {text.errors[banner]}
            </p>
          )}
          <div className="flex items-center gap-3 text-caption text-muted">
            <span aria-hidden="true" className="h-px flex-1 bg-line-10" />
            {text.or}
            <span aria-hidden="true" className="h-px flex-1 bg-line-10" />
          </div>
          <button
            type="button"
            onClick={google}
            disabled={busy !== null}
            className={`${button} border-[1.5px] border-line-20 bg-white text-dark hover:border-line-25 disabled:text-disabled`}
          >
            {text.google}
          </button>
        </div>
      </form>
    </main>
  );
}
